// export default ComponentWiseScore;

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { decodeId } from "../../utills/idUtils";
import { getComponentById } from "../../services/authService";
import FancyLoader from "../../hooks/FancyLoder";

const API_URL = process.env.REACT_APP_API_URL;

const ComponentWiseScore = ({ isGeneratingPdf }) => {
  const { encodedId } = useParams();
  const id = decodeId(encodedId);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asmt_type, setAsmt_type] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/components-score/${id}`);
        if (response.status === 200) {
          // console.log("Response data", response.data);
          setData(response.data);
          const nohasInitiator = response.data.some(
            (item) => item.association_type !== "initiator"
          );
          // console.log("nohasInitiator", nohasInitiator)
          setAsmt_type(nohasInitiator);
        } else {
          throw new Error(`Unexpected response code: ${response.status}`);
        }
      } catch (err) {
        if (err.response) {
          setError(
            `Error ${err.response.status}: ${err.response.data.message || "Something went wrong on the server."
            }`
          );
        } else if (err.request) {
          setError(
            "Network error: No response from the server. Please check your internet connection."
          );
        } else {
          setError(`Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading)
    return (
      <div>
        <FancyLoader />
      </div>
    );
  if (error) return <div className="text-red-600">{error}</div>;

  const groupedData = data.reduce((acc, item) => {
    const {
      pillar_name,
      component_name,
      association_type,
      "sum(aua.answer_value)": score,
      component_id,
    } = item;
    const parsedScore = parseFloat(score) || 0;

    if (!acc[pillar_name]) {
      acc[pillar_name] = { initiators: {}, participants: {} };
    }

    if (association_type === "initiator") {
      acc[pillar_name].initiators[component_name] = {
        score: parsedScore,
        component_id,
      };
    } else if (association_type === "participant") {
      acc[pillar_name].participants[component_name] =
        acc[pillar_name].participants[component_name] || [];
      acc[pillar_name].participants[component_name].push(parsedScore);
    }

    return acc;
  }, {});
  const averageScores = {};
  for (const pillar in groupedData) {
    for (const component in groupedData[pillar].participants) {
      const total = groupedData[pillar].participants[component].reduce(
        (sum, score) => sum + score,
        0
      );
      const count = groupedData[pillar].participants[component].length;
      averageScores[pillar] = averageScores[pillar] || {};
      averageScores[pillar][component] = (total / count).toFixed(2);
    }
  }

  const preparedData = Object.keys(groupedData).map((pillar) => ({
    pillar,
    components: Object.keys(groupedData[pillar].initiators).map(
      (component) => ({
        name: component,
        yourScore: groupedData[pillar].initiators[component].score || 0,
        othersScore: averageScores[pillar]?.[component] || null,
        component_id: groupedData[pillar].initiators[component].component_id,
      })
    ),
  }));

  // console.log("preparedData", preparedData)

  return (
    <div className=" rounded-lg  lg:px-16 mt-4">
      <div style={{ pageBreakAfter: "always" }}></div>
      <div
        className="flex flex-col bg-[#F0F0F0]  rounded-lg"
        style={{ background: "rgb(230 230 230 / var(--tw-bg-opacity, 1))" }}
      >
        <LegendD asmt_type={asmt_type} />
        <h3
          className="bg-blue-700 p-2  text-xl text-white"
          style={{ fontFamily: "Helvetica" }}
        >
          SECTION 1 : Element Summary
        </h3>
        <PillarData
          data={preparedData}
          isGeneratingPdf={isGeneratingPdf}
          asmt_type={asmt_type}
        />
      </div>
    </div>
  );
};

const PillarData = ({ data, asmt_type, isGeneratingPdf }) => {
  // console.log("asmt_type", asmt_type)
  const [tooltip, setTooltip] = useState({
    visible: false,
    value: "",
    position: { x: 0, y: 0 },
  });
  const [descriptions, setDescriptions] = useState({});

  const getComponentDescription = async (componentId) => {
    try {
      const componentDetails = await getComponentById(componentId);
      // //console.log("componentDetails", componentDetails.component_description);
      return componentDetails.component_description;
    } catch (error) {
      console.error("Error fetching component description:", error);
      return "No description available."; // Default description if API call fails
    }
  };

  useEffect(() => {
    const fetchDescriptions = async () => {
      const componentDescriptions = {};
      const descriptionPromises = [];

      // Collect all description fetches into promises
      for (const pillarData of data) {
        for (const component of pillarData.components) {
          descriptionPromises.push(
            getComponentDescription(component.component_id).then(
              (description) => {
                componentDescriptions[component.component_id] = description;
              }
            )
          );
        }
      }

      // Wait for all descriptions to be fetched
      await Promise.all(descriptionPromises);
      //console.log("Fetched Descriptions:", componentDescriptions); // Log descriptions
      setDescriptions(componentDescriptions);
    };

    if (data.length > 0) {
      fetchDescriptions();
    }
  }, [data]);

  //console.log("Descriptions state:", descriptions); // Log descriptions in render

  const showTooltip = (e, score) => {
    setTooltip({
      visible: true,
      value: score,
      position: { x: e.clientX, y: e.clientY },
    });
  };

  const hideTooltip = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  if (!Array.isArray(data) || data.length === 0) {
    return <div>No data available</div>;
  }

  return (
    <div className="flex flex-wrap" style={{paddingBottom:'15px'}}>
      {/* <div style={{pageBreakAfter:'always'}}></div> */}
      <h3
        className="text-lg font-semibold bg-blue-300 p-1 w-full  text-gray-800"
        style={{ fontFamily: "Helvetica" }}
      >
        {/* {pillarData.pillar} */}
        <span className="inline-flex items-center space-x-1 ml-2">
          <span className="w-4 h-4 bg-indigo-500 rounded-full inline-block"></span>
          <span style={{ fontWeight: "normal", fontSize: "15px" }}>
            Self Score
          </span>
        </span>

        {asmt_type ? (
          <span className="inline-flex items-center space-x-1 ml-2">
            <span
              className="w-4 h-4 bg-indigo-500 rounded-full inline-block"
              style={{
                background: "rgb(34 211 238 / var(--tw-bg-opacity, 1))",
              }}
            ></span>
            <span style={{ fontWeight: "normal", fontSize: "15px" }}>
              Average Score from Respondents
            </span>
          </span>
        ) : (
          " "
        )}
      </h3>
      {data.map((pillarData, indx) => (
        <div key={pillarData.pillar} className="w-full px-2">
          {/* {asmt_type && pillarData.pillar === "THE RIGHT SKILLS" && (
            <div style={{ pageBreakAfter: "always" }}></div>
          )} */}

          {/* <h3
            className="text-lg font-semibold bg-blue-300 p-1  text-gray-800"
            style={{ fontFamily: "Helvetica" }}
          >
            {pillarData.pillar}
            <span className="inline-flex items-center space-x-1 ml-2">
              <span className="w-4 h-4 bg-indigo-500 rounded-full inline-block"></span>
              <span style={{ fontWeight: "normal", fontSize: "15px" }}>
                Self Score
              </span>
            </span>

            {asmt_type ? (
              <span className="inline-flex items-center space-x-1 ml-2">
                <span
                  className="w-4 h-4 bg-indigo-500 rounded-full inline-block"
                  style={{
                    background: "rgb(34 211 238 / var(--tw-bg-opacity, 1))",
                  }}
                ></span>
                <span style={{ fontWeight: "normal", fontSize: "15px" }}>
                  Average Score from Respondents
                </span>
              </span>
            ) : (
              " "
            )}
          </h3> */}
          {pillarData.components.length === 0 ? (
            <div className="text-gray-500">
              No components available for this pillar
            </div>
          ) : (
            pillarData.components.map((component, index) => {
              const totalScore = 16; // Maximum score
              const formattedYourScore = Math.min(
                parseFloat(component.yourScore + 2) || 0
              );
              const formattedOthersScore = asmt_type
                ? Math.min(parseFloat(component.othersScore) + 2)
                : Math.min(parseFloat(component.othersScore));

              // Log formatted scores for debugging
              //console.log(`Component: ${component.name}, Your Score: ${formattedYourScore}, Others Score: ${formattedOthersScore}`);

              const yourScorePercentage =
                (formattedYourScore / totalScore) * 100;
              const othersScorePercentage =
                (formattedOthersScore / totalScore) * 100;

              return (
                <div key={component.name} className="w-full lg:p-8">
                  {/* Container for Name, Description, and Bars */}
                  <div className="flex flex-col lg:flex-row items-start w-full space-y-2 lg:space-y-0 lg:space-x-4">
                    {/* Component Name and Description */}
                    <div className="w-full lg:w-1/3">
                      <h4

                        className={`text-gray-700 font-medium mt-3`}
                        style={{ fontFamily: "Helvetica" , fontSize: isGeneratingPdf ? '13px' : '16px' }}
                      >
                        <b>{component.name}</b> -{" "}
                        {descriptions[component.component_id] ||
                          "No description available."}
                      </h4>
                      {/* <p className="text-gray-600" style={{fontSize:'15px', fontFamily: "Helvetica"}}>
                        {descriptions[component.component_id] ||
                          "No description available."} 
                      </p> */}
                    </div>

                    {/* Score Bars */}
                    <div className="w-full lg:w-2/3">
                      {/* <div className="flex flex-row items-center space-x-3">
                                        <span className={`text-gray-800 text-xs min-w-[45px] h-6 flex items-center justify-end  rounded px-2 ${isGeneratingPdf ? "mt-2" : "mt-4"}`}>

                                        <br/>
                                        </span>
                                        </div> */}
                      {/* Your Score Row */}
                      <div className="flex flex-row items-center space-x-3">
                        {/* Score on the Left */}
                        <span
                          className={`text-gray-800 text-xs min-w-[45px]  flex items-center justify-end  rounded px-2 ${isGeneratingPdf ? "mt-1 h-3" : "mt-1 h-6"
                            }`}
                        >
                          {/* {((formattedYourScore / 16) * 5).toFixed(2)} */}
                        </span>
                        {/* Bar with Labels */}
                        <div className="flex-1">
                          <div
                            className={`bar-labels flex justify-between ${isGeneratingPdf ? "mb-3" : "mb-1"
                              }`}
                          >
                            {[
                              `     `,
                              `1-Strongly Disagree`,
                              `2-Disagree`,
                              `3-Neutral`,
                              `4-Agree`,
                              `5-Strongly Agree`,
                            ].map((label) => (
                              <span
                                key={label}
                                className="text-xs text-gray-500"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                          <div className={`relative w-full  ${isGeneratingPdf ? "h-4" : "h-6"} bg-gray-300 rounded`}>
                            {/* <div
                              className="absolute top-0 left-0 h-full bg-indigo-500 rounded"
                              style={{ width: `${yourScorePercentage}%` }}
                              onMouseEnter={(e) =>
                                showTooltip(e, formattedYourScore)
                              }
                              onMouseLeave={hideTooltip}
                            /> */}

                            <table style={{ width: "100%" }}>
                              <tbody>
                                <tr>
                                  <td style={{ width: "100%", padding: 0 }}>
                                    <div
                                      style={{
                                        width: `${yourScorePercentage}%`,
                                        backgroundColor: "#6366f1", // Tailwind's indigo-500
                                        height: isGeneratingPdf ? "15px" : "24px",
                                        borderRadius: "4px",
                                        textAlign: "left",
                                        color: "white",
                                        paddingRight: "4px",
                                        paddingLeft: "4px",
                                       fontSize:  isGeneratingPdf ? "12px" : "15px",
                                      }}
                                    >
                                      {((formattedYourScore / 16) * 5).toFixed(
                                        2
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      {/* Others Score Row (Only if Available) */}

                      {formattedOthersScore > 0 && (
                        <div className="flex flex-row items-center space-x-3 mt-2">
                          {/* Score on the Left - Now matches bar height */}
                          <span
                            className={`text-gray-800 text-xs min-w-[45px]  flex items-center justify-end  rounded px-2 mb-1 
                              ${isGeneratingPdf ? "mb-3 h-3" : "mt-1 h-6"}`}
                          >
                            {/* {((formattedOthersScore / 16) * 5).toFixed(2)} */}
                          </span>
                          {/* Bar */}
                          <div className="flex-1">
                            <div className={`relative w-full  ${isGeneratingPdf ? "h-4" : "h-6"} bg-gray-300 rounded`}>
                              {/* <div
                                className="absolute top-0 left-0 h-full bg-[#3FC8E4] rounded"
                                style={{ width: `${othersScorePercentage}%` }}
                                onMouseEnter={(e) =>
                                  showTooltip(e, formattedOthersScore)
                                }
                                onMouseLeave={hideTooltip}



                              /> */}
                              <table style={{ width: "100%" }}>
                                <tbody>
                                  <tr>
                                    <td style={{ width: "100%", padding: 0 }}>
                                      <div
                                        style={{
                                          width: `${othersScorePercentage}%`,
                                          backgroundColor: "#3FC8E4", // Tailwind's indigo-500
                                          height: isGeneratingPdf ? "15px" : "24px",
                                          borderRadius: "4px",
                                          textAlign: "left",
                                          color: "white",
                                          paddingRight: "4px",
                                          paddingLeft: "4px",
                                         fontSize:  isGeneratingPdf ? "12px" : "15px",
                                        }}
                                        onMouseEnter={(e) =>
                                          showTooltip(e, formattedOthersScore)
                                        }
                                        onMouseLeave={hideTooltip}
                                      >
                                        {(
                                          (formattedOthersScore / 16) *
                                          5
                                        ).toFixed(2)}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ))}
    </div>
  );
};

const LegendD = ({ asmt_type }) => (
  <div
    className="lg:flex items-center justify-start gap-6 p-4"
    style={{ display: "flex" }}
  >
    {asmt_type ? " " : " "}
  </div>
);

export default ComponentWiseScore;
